import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2Icon, LandmarkIcon, PlusIcon, Trash2Icon } from 'lucide-react'

import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import {
  formatNumericInput, numericInputValue, parseNumericInput,
} from '@/lib/currency'
import type { CaptureExtras, DebtScheduleItem, EquityBridgeCapture } from '@/types/capture'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * FIELDS-1 P1b — enterprise value and debt-like items intake.
 *
 * build_equity_bridge() takes nine inputs and the handler was calling it with
 * three: the executive summary's enterprise value and a low/high range derived
 * from it. Funded debt, the debt schedule, deferred revenue, customer deposits,
 * the balance sheet and the NWC peg were all left at their defaults, so every
 * deal's bridge could only ever conclude "equity value = enterprise value" and
 * print a list of the items it knew it was missing. Nothing in the product asked
 * for any of them. This is that form.
 *
 * The enterprise value here is a REAL valuation input, distinct from the
 * engagement sizing proxy captured at intake — that figure exists to scope the
 * engagement, not to price the deal. When both are present, this one wins, and
 * the card names which one it used.
 */

const SCALAR_FIELDS = [
  'enterprise_value',
  'enterprise_value_low',
  'enterprise_value_high',
  'funded_debt',
  'deferred_revenue',
  'customer_deposits',
] as const

type ScalarField = typeof SCALAR_FIELDS[number]

const SCALAR_LABELS: Record<ScalarField, string> = {
  enterprise_value: 'Enterprise Value',
  enterprise_value_low: 'EV — Low',
  enterprise_value_high: 'EV — High',
  funded_debt: 'Total Funded Debt',
  deferred_revenue: 'Deferred Revenue',
  customer_deposits: 'Customer Deposits',
}

const SCALAR_HINTS: Record<ScalarField, string> = {
  enterprise_value: 'The negotiated or concluded EV. Required before a bridge is computed.',
  enterprise_value_low: 'Leave blank to use the point estimate as the low end.',
  enterprise_value_high: 'Leave blank to use the point estimate as the high end.',
  funded_debt: 'Overrides the schedule total below. Blank uses the schedule, then the balance sheet.',
  deferred_revenue: 'Blank falls back to the balance sheet, if one has been entered.',
  customer_deposits: 'Blank falls back to the balance sheet, if one has been entered.',
}

interface NormalizationEnvelope {
  form_extras_json?: CaptureExtras | null
}

/** A schedule row while it is being edited: amount is a draft string, and the
 *  local id keeps React keys stable across reorders and deletes. */
interface DebtRow {
  key: string
  description: string
  amount: string
  note: string
}

interface Props {
  dealId: string
  /** Called after a successful save so the caller can re-read the bridge. */
  onSaved?: () => void
}

let rowSeq = 0
function newRow(): DebtRow {
  rowSeq += 1
  return { key: `debt-${rowSeq}`, description: '', amount: '', note: '' }
}

export function EquityBridgeInputs({ dealId, onSaved }: Props) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // Draft strings, not numbers — a controlled numeric input eats a trailing "."
  // as it is typed, so "1,250,000.50" would be impossible to enter.
  const [scalars, setScalars] = useState<Record<ScalarField, string>>(
    () => Object.fromEntries(SCALAR_FIELDS.map(f => [f, ''])) as Record<ScalarField, string>,
  )
  const [rows, setRows] = useState<DebtRow[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiClient.get<NormalizationEnvelope>(
        `/qoe/deals/${dealId}/normalization`,
      )
      const block: EquityBridgeCapture = res.data.form_extras_json?.equity_bridge ?? {}
      setScalars(
        Object.fromEntries(
          SCALAR_FIELDS.map(f => {
            const val = block[f]
            return [f, val != null ? numericInputValue(val) : '']
          }),
        ) as Record<ScalarField, string>,
      )
      setRows(
        (block.debt_schedule ?? []).map((item: DebtScheduleItem) => ({
          ...newRow(),
          description: item.description ?? '',
          amount: item.amount != null ? numericInputValue(item.amount) : '',
          note: item.note ?? '',
        })),
      )
    } catch {
      // No normalization session yet is the ordinary early state of a deal, not
      // a failure — the form simply opens empty.
    } finally {
      setLoading(false)
    }
  }, [dealId])

  useEffect(() => { void load() }, [load])

  const setScalar = (field: ScalarField, raw: string) => {
    setScalars(prev => ({ ...prev, [field]: formatNumericInput(raw) }))
  }

  const setRow = (key: string, patch: Partial<Omit<DebtRow, 'key'>>) => {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)))
  }

  const scheduleTotal = rows.reduce((sum, r) => {
    const parsed = parseNumericInput(r.amount)
    return sum + (parsed === '' ? 0 : parsed)
  }, 0)

  /** A row with an amount but no description would land in the bridge as an
   *  unlabelled deduction from equity value. Block the save instead — the seller
   *  has to be able to confirm or dispute each line. */
  const unlabelledRows = rows.filter(
    r => r.description.trim() === '' && parseNumericInput(r.amount) !== '',
  )

  const save = async () => {
    if (unlabelledRows.length > 0) {
      toast.error('Every debt schedule line needs a description before it can be saved.')
      return
    }
    setSaving(true)
    try {
      const payload: Record<string, unknown> = {}
      for (const field of SCALAR_FIELDS) {
        const parsed = parseNumericInput(scalars[field])
        // null is "not disclosed", which is what makes the bridge fall back to
        // the schedule or the balance sheet. A zero would suppress that fallback
        // while contributing nothing, so a blank must never become 0.
        payload[field] = parsed === '' ? null : parsed
      }
      // Rows with neither a description nor an amount are just empty form rows.
      // Sending [] deliberately clears a previously-saved schedule.
      payload.debt_schedule = rows
        .filter(r => r.description.trim() !== '' || parseNumericInput(r.amount) !== '')
        .map(r => {
          const parsed = parseNumericInput(r.amount)
          return {
            description: r.description.trim(),
            amount: parsed === '' ? 0 : parsed,
            note: r.note.trim() === '' ? null : r.note.trim(),
          }
        })

      // Only the equity_bridge block. The save route is preserve-on-omit and
      // form_extras_json is merged rather than replaced, so this cannot disturb
      // the P&L grid, the balance sheet, or the proof-of-cash figures.
      await apiClient.put(`/qoe/deals/${dealId}/normalization`, { equity_bridge: payload })
      toast.success('Equity bridge inputs saved')
      onSaved?.()
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-3 flex flex-col gap-3">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="h-28 w-full" />
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-card p-3 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <LandmarkIcon className="h-4 w-4 text-brand-primary" />
        <h2 className="text-sm font-semibold text-brand-text">Equity Bridge Inputs</h2>
      </div>

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Enterprise value less debt-like items, plus cash above the working capital peg,
        equals equity value. Enter figures as reported and leave anything undisclosed
        blank — blank is <span className="font-medium text-brand-text">not</span> zero, and a
        blank is what lets the bridge fall back to the debt schedule or the balance sheet.
        The working-capital true-up is derived from the NWC peg, not entered here.
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {SCALAR_FIELDS.map(field => (
          <div key={field} className="flex flex-col gap-1">
            <label
              htmlFor={`eb-${field}`}
              className="text-[11px] font-medium text-brand-text"
            >
              {SCALAR_LABELS[field]}
            </label>
            <Input
              id={`eb-${field}`}
              type="text"
              inputMode="decimal"
              value={scalars[field]}
              onChange={e => setScalar(field, e.target.value)}
              placeholder="—"
              className="h-7 text-right text-xs tabular-nums"
            />
            <p className="text-[10px] leading-snug text-muted-foreground">
              {SCALAR_HINTS[field]}
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2 border-t border-border pt-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xs font-semibold text-brand-text">Debt Schedule</h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRows(prev => [...prev, newRow()])}
            className="h-7 gap-1.5 text-xs"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add line
          </Button>
        </div>

        <p className="text-[11px] leading-relaxed text-muted-foreground">
          One line per instrument, named as the seller and their lender would name it
          (&ldquo;SBA Loan #1234567&rdquo;). The bridge lists these individually, so a single
          &ldquo;total debt&rdquo; figure is not something the seller can confirm.
        </p>

        {rows.length === 0 ? (
          <p className="text-[11px] italic text-muted-foreground">
            No debt schedule entered. The bridge will fall back to Total Funded Debt above,
            then to the balance sheet, and will flag the schedule as still needed.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-1 pr-3 font-normal">Description</th>
                  <th className="py-1 pr-3 font-normal text-right">Amount</th>
                  <th className="py-1 pr-3 font-normal">Note</th>
                  <th className="py-1 font-normal sr-only">Remove</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.key} className="border-b last:border-0">
                    <td className="py-1 pr-3">
                      <Input
                        type="text"
                        value={row.description}
                        onChange={e => setRow(row.key, { description: e.target.value })}
                        aria-label="Debt description"
                        placeholder="SBA Loan #1234567"
                        className="h-7 min-w-44 text-xs"
                      />
                    </td>
                    <td className="py-1 pr-3">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={row.amount}
                        onChange={e => setRow(row.key, { amount: formatNumericInput(e.target.value) })}
                        aria-label="Debt amount"
                        placeholder="—"
                        className="h-7 w-32 ml-auto text-right text-xs tabular-nums"
                      />
                    </td>
                    <td className="py-1 pr-3">
                      <Input
                        type="text"
                        value={row.note}
                        onChange={e => setRow(row.key, { note: e.target.value })}
                        aria-label="Debt note"
                        placeholder="Payoff at close"
                        className="h-7 min-w-40 text-xs"
                      />
                    </td>
                    <td className="py-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setRows(prev => prev.filter(r => r.key !== row.key))}
                        aria-label={`Remove ${row.description || 'debt line'}`}
                        className="h-7 w-7"
                      >
                        <Trash2Icon className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t">
                  <td className="py-1.5 pr-3 font-medium">Schedule total</td>
                  <td className="py-1.5 pr-3 text-right font-medium tabular-nums">
                    {scheduleTotal.toLocaleString('en-US', {
                      style: 'currency', currency: 'USD',
                      minimumFractionDigits: 0, maximumFractionDigits: 0,
                    })}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-muted-foreground">
          {unlabelledRows.length > 0
            ? `${unlabelledRows.length} debt line${unlabelledRows.length > 1 ? 's' : ''} still need${unlabelledRows.length > 1 ? '' : 's'} a description.`
            : parseNumericInput(scalars.enterprise_value) === ''
              ? 'The bridge stays pending until an enterprise value is entered.'
              : 'Saved figures feed the bridge below on the next read.'}
        </p>
        <Button size="sm" onClick={save} disabled={saving} className="h-7 gap-1.5 text-xs">
          {saving && <Loader2Icon className="h-3.5 w-3.5 animate-spin" />}
          Save Equity Bridge Inputs
        </Button>
      </div>
    </div>
  )
}

export default EquityBridgeInputs
